import { NavLink, useNavigate } from 'react-router-dom';
import { ArrowLeft, BarChart3, Beaker, FolderKey, LogOut, MessageSquare, ShieldCheck, ShieldOff, SlidersHorizontal, Users } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../lib/auth';
import { ThemeToggle } from '../ui/theme-toggle';

const ADMIN_NAV = [
  // First: what an administrator opens to see whether Memory is working at all.
  { to: '/admin/metrics', icon: BarChart3, label: 'Metrics' },
  { to: '/admin/features', icon: SlidersHorizontal, label: 'Feature Permissions' },
  { to: '/admin/users', icon: Users, label: 'Accounts' },
  // Every project role one person holds, on one page — the per-project Access tab needs a seat as
  // that project's ADMIN, and shows one project at a time.
  { to: '/admin/access', icon: FolderKey, label: 'Project Access' },
  // Not a user-management screen like the two above, but administrative for the same reason: it
  // spans every project and every reporter, and its whole job is deciding what to act on.
  { to: '/admin/feedback', icon: MessageSquare, label: 'Ingest Feedback' },
  // What the ingestor kept out of the knowledge base. Here rather than beside the project pages
  // because it holds the withheld text itself — the one thing members must not be able to read.
  { to: '/admin/guardrail', icon: ShieldOff, label: 'Guardrail Log' },
  // Beside the feedback queue rather than under the workspace nav, and for the same reason it is:
  // this is where the agents are judged. Feedback is what people said went wrong; the playground is
  // where you find out whether a change to the prompt or the tool roster fixes it.
  { to: '/admin/playground', icon: Beaker, label: 'Agent Playground' },
];

/**
 * Admin-area sidebar. Visually distinct from the project sidebar — slate accent rather than
 * orange — because the consequences here are workspace-wide, and it should never be ambiguous
 * which set of controls you're looking at.
 */
export function AdminSidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <aside className="fixed left-0 top-0 h-full w-56 bg-card border-r border-border flex flex-col z-40">
      <div className="px-5 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-ink flex items-center justify-center">
            <ShieldCheck size={15} className="text-surface" />
          </div>
          <div>
            <div className="text-sm font-bold text-ink leading-tight">Administration</div>
            <div className="text-[10px] text-ink-faint uppercase tracking-wide">System settings</div>
          </div>
        </div>
      </div>

      <div className="px-3 py-3 border-b border-border">
        <NavLink
          to="/"
          className="flex items-center gap-2 px-2 py-2 rounded-lg text-xs text-ink-muted hover:bg-surface-subtle hover:text-ink transition-colors"
        >
          <ArrowLeft size={13} />
          Back to workspace
        </NavLink>
      </div>

      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        <p className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wide text-ink-faint">Manage</p>
        <div className="space-y-0.5">
          {ADMIN_NAV.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-surface-muted text-ink'
                    : 'text-ink-muted hover:bg-surface-subtle hover:text-ink',
                )
              }
            >
              <Icon size={16} />
              {label}
            </NavLink>
          ))}
        </div>
      </nav>

      <div className="px-3 py-4 border-t border-border space-y-2">
        <div className="px-2">
          <ThemeToggle />
        </div>
        {user && (
          <div className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-surface-subtle group">
            <div className="w-7 h-7 rounded-full bg-ink flex items-center justify-center flex-shrink-0">
              <ShieldCheck size={13} className="text-surface" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-ink-secondary truncate">{user.name ?? user.email}</p>
              <p className="text-[10px] text-ink-faint truncate">System admin</p>
            </div>
            <button
              onClick={handleLogout}
              title="Sign out"
              className="opacity-0 group-hover:opacity-100 transition-opacity text-ink-faint hover:text-red-500 dark:hover:text-red-400"
            >
              <LogOut size={14} />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
